import { useRef, useState } from "react";
import { motion, useReducedMotion, useScroll, useMotionValueEvent } from "framer-motion";
import { Reveal, SectionTag, EASE, MaskedLines } from "@/components/Reveal";

const STEPS = [
  { label: "Discover", copy: "Customer finds the service through digital channels." },
  { label: "Enquire", copy: "Customer submits a requirement or enquiry." },
  { label: "Qualify", copy: "The requirement enters structured CRM and operational workflows." },
  { label: "Match / Fulfil", copy: "The relevant service network fulfils the requirement." },
  { label: "Transact", copy: "Digital commercial and payment processes support the transaction where applicable." },
  { label: "Support", copy: "Customer communication and service support continue through the lifecycle." },
  { label: "Improve", copy: "Feedback and operating data inform future improvements." },
];

export default function Journey() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const [active, setActive] = useState(-1);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 0.8", "end 0.6"],
  });
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    setActive(Math.min(STEPS.length - 1, Math.floor(v * (STEPS.length + 0.5))));
  });
  const isActive = (i) => reduce || i <= active;

  return (
    <section
      id="journey"
      ref={ref}
      data-testid="journey-section"
      className="relative scroll-mt-20 overflow-hidden bg-[#070604] py-20 sm:py-24"
    >
      <div
        className="pointer-events-none absolute left-1/2 top-[30%] h-[40vh] w-[70vw] -translate-x-1/2 rounded-full bg-[#B8962E]/[0.08] blur-[140px]"
        aria-hidden="true"
      />
      <div className="grain-overlay pointer-events-none absolute inset-0" aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <SectionTag index="07">From Demand to Delivery</SectionTag>
        </Reveal>
        <h2
          data-testid="journey-headline"
          className="mt-6 font-serif text-3xl font-normal leading-[1.12] tracking-tight text-slate-100 sm:text-4xl lg:text-[44px]/[1.14]"
        >
          <MaskedLines delay={0.1}>
            One connected
            <br />
            <span className="italic text-transparent bg-gradient-to-r from-[#F0DDA6] via-[#E7CE8B] to-[#C9A227] bg-clip-text">
              customer journey.
            </span>
          </MaskedLines>
        </h2>

        {/* desktop: progressive scroll-activated horizontal journey */}
        <div data-testid="journey-flow" className="relative mt-16 hidden lg:block">
          <div className="absolute left-0 right-0 top-[7px] h-[2px] bg-white/10" aria-hidden="true" />
          <motion.div
            className="absolute left-0 top-[7px] h-[2px] origin-left bg-gradient-to-r from-[#E7CE8B] via-[#D4AF37] to-[#D4AF37]/60"
            style={{ scaleX: scrollYProgress, width: "100%" }}
            aria-hidden="true"
          />
          <div className="grid grid-cols-7 gap-6">
            {STEPS.map((s, i) => (
              <div key={s.label} data-testid={`journey-step-${i + 1}`}>
                <span
                  className={`block h-3.5 w-3.5 rounded-full border transition-all duration-700 ${
                    isActive(i)
                      ? "border-[#E7CE8B] bg-[#E7CE8B] shadow-[0_0_16px_rgba(212,175,55,0.55)]"
                      : "border-white/25 bg-[#070604]"
                  }`}
                  aria-hidden="true"
                />
                <p
                  className={`mt-7 text-xs font-medium uppercase tracking-[0.2em] transition-colors duration-700 ${
                    isActive(i) ? "text-[#E7CE8B]" : "text-stone-500"
                  }`}
                >
                  {s.label}
                </p>
                <p
                  className={`mt-3 text-[13px] font-normal leading-relaxed transition-colors duration-700 ${
                    isActive(i) ? "text-stone-300" : "text-stone-600"
                  }`}
                >
                  {s.copy}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* mobile: vertical journey */}
        <div data-testid="journey-flow-mobile" className="relative mt-14 lg:hidden">
          <div className="absolute bottom-2 left-[5px] top-2 w-px bg-gradient-to-b from-[#E7CE8B]/60 via-[#E7CE8B]/30 to-transparent" aria-hidden="true" />
          <div className="space-y-9">
            {STEPS.map((s, i) => (
              <Reveal key={s.label} delay={0.05 * i}>
                <div data-testid={`journey-step-mobile-${i + 1}`} className="flex gap-6">
                  <span className="mt-1 block h-2.5 w-2.5 shrink-0 rounded-full bg-[#E7CE8B] shadow-[0_0_12px_rgba(212,175,55,0.5)]" aria-hidden="true" />
                  <div>
                    <p className="text-xs font-medium uppercase tracking-[0.2em] text-slate-200">
                      {s.label}
                    </p>
                    <p className="mt-2 max-w-md text-sm font-normal leading-relaxed text-stone-400">
                      {s.copy}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
